import { isNoSpeechWarningMidSessionEnabled } from '@/app-shell/flags/no-speech-mid-session-warning-flag';
import { logger } from '@/utils/logger/logger';

/** Captured, transcribed silence after speech; independent of wall-clock pauses. */
export const MID_SESSION_SILENCE_MS = 5 * 60_000;

type WarningPhase = 'waiting' | 'eligible' | 'shown' | 'dismissed' | 'recovered';
type WarningTransport =
  | 'web_sse_v3'
  | 'web_http_v2'
  | 'web_http_v1'
  | 'desktop_v1'
  | 'desktop_v2';
type CompletedChunk = {
  sessionId?: string;
  recordingId: string;
  index: number;
  durationMs: number;
  text: string | null | undefined;
  transport: WarningTransport;
};
type RecordedChunk = { durationMs: number; hasSpeech: boolean };
type WarningState = {
  sessionKey: string;
  phase: WarningPhase;
  transport: WarningTransport;
  warningInstanceId: string;
  pendingChunks: Map<number, RecordedChunk>;
  recentChunks: Map<number, RecordedChunk>;
  processedThrough: number | null;
  hasSpeech: boolean;
  firstObservedAtMs: number;
  shownAtMs?: number;
  silentChunkCount: number;
  silentAudioMs: number;
  suppressionReasons: Set<WarningSuppressionReason>;
};

type WarningSuppressionReason = 'startup_warning' | 'microphone_warning';

const states = new Map<string, WarningState>();
const warnedSessions = new Set<string>();
const sessionsWithSpeech = new Set<string>();
const listeners = new Set<() => void>();
const MAX_TRACKED_RECORDINGS = 100;
const MAX_PENDING_CHUNKS = 128;
const MAX_RECENT_CHUNKS = 64;
const MAX_GAP_WAIT_CHUNKS = 8;
let nextWarningInstance = 0;

const notify = () => listeners.forEach((listener) => listener());

export const subscribeNoSpeechMidSessionWarning = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getNoSpeechMidSessionWarningPhase = (
  recordingId: string | null
): WarningPhase => (recordingId && states.get(recordingId)?.phase) || 'waiting';

const logWarningEvent = (
  state: WarningState,
  tag: string,
  eventType: string,
  details: Record<string, string | number | boolean> = {}
) => {
  logger.info({
    tag,
    event_type: eventType,
    warning_instance_id: state.warningInstanceId,
    transport: state.transport,
    platform: state.transport.startsWith('desktop') ? 'desktop' : 'web',
    transcription_version: state.transport.endsWith('v3')
      ? 'v3'
      : state.transport.endsWith('v2')
        ? 'v2'
        : 'v1',
    silent_chunk_count: state.silentChunkCount,
    silent_audio_ms: state.silentAudioMs,
    ...details,
  });
};

const rememberSpeech = (sessionKey: string) => {
  sessionsWithSpeech.add(sessionKey);
  if (sessionsWithSpeech.size > MAX_TRACKED_RECORDINGS) {
    const oldest = sessionsWithSpeech.values().next().value;
    if (oldest) sessionsWithSpeech.delete(oldest);
  }
};

const rememberProcessedChunk = (
  state: WarningState,
  index: number,
  chunk: RecordedChunk
) => {
  state.recentChunks.set(index, chunk);
  if (state.recentChunks.size > MAX_RECENT_CHUNKS) {
    const oldest = state.recentChunks.keys().next().value;
    if (oldest !== undefined) state.recentChunks.delete(oldest);
  }
};

const recoverFromSpeech = (state: WarningState) => {
  if (state.phase === 'shown' || state.phase === 'dismissed') {
    const now = Date.now();
    logWarningEvent(
      state,
      'no_speech_mid_session_recovered',
      'NO_SPEECH_MID_SESSION_RECOVERED',
      state.shownAtMs === undefined ? {} : { shown_to_recovery_ms: now - state.shownAtMs }
    );
    state.phase = 'recovered';
    notify();
  } else if (state.phase === 'eligible') {
    logWarningEvent(
      state,
      'no_speech_mid_session_withdrawn',
      'NO_SPEECH_MID_SESSION_WITHDRAWN'
    );
    state.phase = 'waiting';
    notify();
  }
  state.silentChunkCount = 0;
  state.silentAudioMs = 0;
};

const processPendingSilence = (state: WarningState) => {
  while (state.processedThrough !== null) {
    const nextExpectedIndex = state.processedThrough + 1;
    if (!state.pendingChunks.has(nextExpectedIndex)) {
      if (state.pendingChunks.size < MAX_GAP_WAIT_CHUNKS) break;
      const nextAvailableIndex = Math.min(...state.pendingChunks.keys());
      if (nextAvailableIndex <= nextExpectedIndex) break;
      logWarningEvent(
        state,
        'no_speech_mid_session_gap_skipped',
        'NO_SPEECH_MID_SESSION_GAP_SKIPPED',
        {
          missing_chunk_count: nextAvailableIndex - nextExpectedIndex,
          pending_chunk_count: state.pendingChunks.size,
        }
      );
      // The missing chunk may contain speech. Treat the gap as a conservative
      // boundary so silence before it is never combined with silence after it.
      state.silentChunkCount = 0;
      state.silentAudioMs = 0;
      state.processedThrough = nextAvailableIndex - 1;
    }
    const nextIndex = state.processedThrough + 1;
    const next = state.pendingChunks.get(nextIndex)!;
    state.pendingChunks.delete(nextIndex);
    state.processedThrough = nextIndex;
    rememberProcessedChunk(state, nextIndex, next);
    if (state.phase === 'dismissed') continue;
    state.silentChunkCount++;
    state.silentAudioMs += next.durationMs;
    if (state.phase === 'waiting' && state.silentAudioMs > MID_SESSION_SILENCE_MS) {
      state.phase = 'eligible';
      logWarningEvent(
        state,
        'no_speech_mid_session_eligible',
        'NO_SPEECH_MID_SESSION_ELIGIBLE',
        { time_to_eligibility_ms: Date.now() - state.firstObservedAtMs }
      );
      notify();
    }
  }
};

const applySpeechCorrection = (
  state: WarningState,
  sessionKey: string,
  index: number,
  chunk: RecordedChunk
) => {
  rememberProcessedChunk(state, index, chunk);
  state.hasSpeech = true;
  rememberSpeech(sessionKey);

  let expectedIndex = index + 1;
  let silentChunkCount = 0;
  let silentAudioMs = 0;
  const laterChunks = [...state.recentChunks.entries()]
    .filter(([chunkIndex]) => chunkIndex > index)
    .sort(([left], [right]) => left - right);
  for (const [chunkIndex, laterChunk] of laterChunks) {
    if (chunkIndex !== expectedIndex) break;
    expectedIndex++;
    if (laterChunk.hasSpeech) {
      silentChunkCount = 0;
      silentAudioMs = 0;
      continue;
    }
    silentChunkCount++;
    silentAudioMs += laterChunk.durationMs;
  }

  state.silentChunkCount = silentChunkCount;
  state.silentAudioMs = silentAudioMs;
  if (silentAudioMs > MID_SESSION_SILENCE_MS) {
    if (state.phase === 'waiting') {
      state.phase = 'eligible';
      logWarningEvent(
        state,
        'no_speech_mid_session_eligible',
        'NO_SPEECH_MID_SESSION_ELIGIBLE',
        { time_to_eligibility_ms: Date.now() - state.firstObservedAtMs }
      );
      notify();
    }
    return;
  }

  recoverFromSpeech(state);
};

/** Called only for successfully transcribed chunks, never staged or failed ones. */
export const observeNoSpeechMidSessionCompletedChunk = ({
  sessionId,
  recordingId,
  index,
  durationMs,
  text,
  transport,
}: CompletedChunk): void => {
  try {
    if (
      !isNoSpeechWarningMidSessionEnabled() ||
      !recordingId ||
      !Number.isInteger(index) ||
      index < 0 ||
      typeof text !== 'string'
    )
      return;

    const sessionKey = sessionId || recordingId;
    let state = states.get(recordingId);
    if (!state) {
      if (warnedSessions.has(sessionKey)) return;
      state = {
        sessionKey,
        phase: 'waiting',
        transport,
        warningInstanceId:
          globalThis.crypto?.randomUUID?.() ??
          `local-${Date.now()}-${++nextWarningInstance}`,
        pendingChunks: new Map(),
        recentChunks: new Map(),
        processedThrough: null,
        hasSpeech: sessionsWithSpeech.has(sessionKey),
        firstObservedAtMs: Date.now(),
        silentChunkCount: 0,
        silentAudioMs: 0,
        suppressionReasons: new Set(),
      };
      states.set(recordingId, state);
      if (states.size > MAX_TRACKED_RECORDINGS) {
        const oldestId = states.keys().next().value;
        if (oldestId) states.delete(oldestId);
      }
    }
    if (state.phase === 'recovered') return;
    const hasSpeech = Boolean(text.trim());
    const chunk = {
      durationMs: Number.isFinite(durationMs) ? Math.max(0, durationMs) : 0,
      hasSpeech,
    };
    const processed = state.recentChunks.get(index);
    if (processed) {
      if (processed.hasSpeech || !hasSpeech) return;
      // A completed transcript may gain words on refresh. Rebuild the bounded
      // trailing run so later silence can keep a legitimately eligible warning.
      applySpeechCorrection(state, sessionKey, index, chunk);
      return;
    }
    if (state.processedThrough !== null && index <= state.processedThrough) return;

    if (hasSpeech) {
      // The newest speech anchors the next silent run even if older chunks
      // have not arrived yet. Earlier pending silence cannot count after it.
      for (const pendingIndex of state.pendingChunks.keys()) {
        if (pendingIndex <= index) state.pendingChunks.delete(pendingIndex);
      }
      state.processedThrough = index;
      rememberProcessedChunk(state, index, chunk);
      state.hasSpeech = true;
      rememberSpeech(sessionKey);
      const warningWasVisible = state.phase === 'shown' || state.phase === 'dismissed';
      recoverFromSpeech(state);
      if (!warningWasVisible) processPendingSilence(state);
      return;
    }

    if (state.hasSpeech && state.processedThrough === null)
      state.processedThrough = index - 1;
    if (state.pendingChunks.has(index)) return;
    state.pendingChunks.set(index, chunk);
    if (state.pendingChunks.size > MAX_PENDING_CHUNKS) {
      const farthest = Math.max(...state.pendingChunks.keys());
      state.pendingChunks.delete(farthest);
    }
    if (state.hasSpeech) processPendingSilence(state);
  } catch {
    // Passive observation must never fail an upload or transcript refresh.
    try {
      logger.warn('Unable to observe mid-session speech state');
    } catch {
      // Telemetry cannot affect recording.
    }
  }
};

export const markNoSpeechMidSessionWarningShown = (
  recordingId: string,
  alertSoundPlayback?: Promise<boolean>
): void => {
  const state = states.get(recordingId);
  if (state?.phase !== 'eligible') return;
  state.phase = 'shown';
  warnedSessions.add(state.sessionKey);
  if (warnedSessions.size > MAX_TRACKED_RECORDINGS) {
    const oldestId = warnedSessions.values().next().value;
    if (oldestId) warnedSessions.delete(oldestId);
  }
  state.shownAtMs = Date.now();
  const logShown = (alertSoundPlayed: boolean) =>
    logWarningEvent(state, 'no_speech_mid_session_shown', 'NO_SPEECH_MID_SESSION_SHOWN', {
      time_to_show_ms: state.shownAtMs! - state.firstObservedAtMs,
      alert_sound_played: alertSoundPlayed,
    });
  if (alertSoundPlayback) {
    void alertSoundPlayback.then(logShown, () => logShown(false));
  } else {
    logShown(false);
  }
  notify();
};

export const markNoSpeechMidSessionWarningSuppressed = (
  recordingId: string,
  reason: WarningSuppressionReason
): void => {
  const state = states.get(recordingId);
  if (state?.phase !== 'eligible' || state.suppressionReasons.has(reason)) return;
  state.suppressionReasons.add(reason);
  logWarningEvent(
    state,
    'no_speech_mid_session_suppressed',
    'NO_SPEECH_MID_SESSION_SUPPRESSED',
    { suppression_reason: reason }
  );
};

/** Avoids transcript refresh work after a recording can no longer warn again. */
export const shouldObserveNoSpeechMidSession = (
  sessionId: string,
  recordingId?: string | null
): boolean => {
  const state = recordingId
    ? states.get(recordingId)
    : [...states.values()].find((candidate) => candidate.sessionKey === sessionId);
  return state ? state.phase !== 'recovered' : !warnedSessions.has(sessionId);
};

export const dismissNoSpeechMidSessionWarning = (recordingId: string): void => {
  const state = states.get(recordingId);
  if (state?.phase !== 'shown') return;
  state.phase = 'dismissed';
  logWarningEvent(
    state,
    'no_speech_mid_session_dismissed',
    'NO_SPEECH_MID_SESSION_DISMISSED'
  );
  notify();
};

/** Reset detector state for isolated tests. */
export const resetNoSpeechMidSessionWarningState = () => {
  states.clear();
  warnedSessions.clear();
  sessionsWithSpeech.clear();
  listeners.clear();
};
